import { create } from './store';

/** Closest available plan to the remembered one; ties go to the lower plan. */
export const nearestPlan = (wanted: number, available: number[]): number =>
  [...available]
    .sort((a, b) => a - b)
    .reduce((best, p) => (Math.abs(p - wanted) < Math.abs(best - wanted) ? p : best));

export const resolvePlan = (
  remembered: number | null,
  available: number[],
): { plan: number | null; overridden: boolean } => {
  if (remembered === null || available.length === 0) return { plan: null, overridden: false };
  if (available.includes(remembered)) return { plan: remembered, overridden: false };
  return { plan: nearestPlan(remembered, available), overridden: true };
};

type State = {
  open: boolean;
  rememberedPlan: number | null;
  /** Thread the remembered plan belongs to; null while the conversation has no thread yet. */
  conversationThreadId: string | null;
};
type Actions = {
  setOpen: (open: boolean) => void;
  setRememberedPlan: (plan: number) => void;
  resetRememberedPlan: () => void;
  /**
   * Scope the remembered plan to the current conversation: a plan chosen before the
   * first message (no thread yet) follows the thread that message creates; opening
   * the dialog in a different thread starts fresh.
   */
  syncConversation: (threadId: string | undefined) => void;
};

// The remembered plan lives as long as the conversation (see syncConversation);
// createNewChat also resets it. Never persisted per advisor.
const guidedPortfoliosStore = create<State & Actions>()((set, get) => ({
  open: false,
  rememberedPlan: null,
  conversationThreadId: null,
  setOpen: (open) => set({ open }),
  setRememberedPlan: (plan) => set({ rememberedPlan: plan }),
  resetRememberedPlan: () => set({ rememberedPlan: null, conversationThreadId: null }),
  syncConversation: (threadId) => {
    const current = threadId ?? null;
    const owner = get().conversationThreadId;
    if (owner === current) return;
    if (owner === null && current !== null) {
      set({ conversationThreadId: current });
      return;
    }
    set({ rememberedPlan: null, conversationThreadId: current });
  },
}));

export default guidedPortfoliosStore;
